import { ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Dropdown, IconButton } from "@carbon/react";
import {
  Delivery,
  Notification,
  SendAlt,
  View,
  WarningAltFilled,
} from "@carbon/icons-react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
  PAGE_SIZES,
} from "../../components/carbon/DataTable/DataTable";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import { useNotification, useUser } from "../../context";
import useDebounce from "../../hooks/useDebounce";
import { usePurchaseOrders } from "../../hooks/usePurchaseOrders";
import { usePurchaseOrderCounts } from "../../hooks/usePurchaseOrderCounts";
import {
  useRemindApprover,
  useResendOc,
} from "../../hooks/useOcActions";
import { PurchaseOrderListItem, PurchaseOrderStatus } from "../../types";
import { ABILITIES } from "../../utils";
import { formatDate } from "../../utils/utils";
import CreateOcModal from "./CreateOc/CreateOcModal";
import OcDetailModal from "./OcDetail/OcDetailModal";
import OcStatTiles from "./components/OcStatTiles";
import OcStatusPill from "./components/OcStatusPill";
import { isOcOverdue } from "./ocOverdue";
import { formatOrderTotal } from "./formatOrderTotal";
import "./purchaseOrders.scss";

const ALL_STATUSES = "ALL";

/** DataTable column key → the API's sort key (the BE whitelist). Only these columns are sortable;
 * `purchaseRequest` and `actions` have no column here and stay plain. */
const SORT_KEY_BY_COLUMN: Record<string, string> = {
  code: "code",
  supplier: "supplier",
  total: "totalAmount",
  createdAt: "createdAt",
  requestedDelivery: "requestedDeliveryDate",
  status: "status",
};

/** The user-facing status filter options, in the prototype's order: the six buyer-meaningful
 * statuses, then DRAFT/CANCELLED (ours). REJECTED_APPROVER has no chip — it stays reachable via
 * "Todos" (both rejections read as "Rechazada"; the API filters by a single status). */
const PO_FILTER_STATUSES: PurchaseOrderStatus[] = [
  "PENDING_APPROVAL",
  "SENT",
  "ACCEPTED",
  "IN_DELIVERY",
  "DELIVERED",
  "REJECTED_SUPPLIER",
  "DRAFT",
  "CANCELLED",
];

interface PurchaseOrderRow extends DataTableRow {
  code: string;
  purchaseRequest: ReactNode;
  supplier: string;
  total: string;
  createdAt: string;
  requestedDelivery: ReactNode;
  status: ReactNode;
  actions: ReactNode;
}

/**
 * Purchase orders (OC) list. Filtering, search and pagination are ALL server-side. The state filter
 * sits in a bar above the table (the Orders/Materials pattern); search stays in the DataTable
 * toolbar. Each row carries a contextual actions menu (view / send / resend / coordinate delivery /
 * remind approver), shown per the OC's status + the current user's role in it.
 */
const PurchaseOrdersPage = () => {
  const { t } = useTranslation();
  const { can, user } = useUser();
  const { openNotification } = useNotification();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  // The OC shown in the detail dialog (null = closed); rows and the "Ver" icon open it. A deep-link
  // (/purchase-orders/:id) seeds it, so the dialog opens over the list; closing returns to the list URL.
  const { id: routeOcId } = useParams<{ id: string }>();
  const [detailOcId, setDetailOcId] = useState<string | null>(
    routeOcId ?? null
  );

  const [status, setStatus] = useState<PurchaseOrderStatus | undefined>();
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm } = useDebounce(searchTerm, 400);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  // The sorted COLUMN key (the DataTable's own key); default matches the API's createdAt/desc.
  const [sortColumn, setSortColumn] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // A new search or status always restarts at the first page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, status]);

  const { data, isLoading } = usePurchaseOrders({
    status,
    q: debouncedSearchTerm || undefined,
    sort: SORT_KEY_BY_COLUMN[sortColumn],
    direction: sortDirection,
    page,
    size: pageSize,
  });

  // Clicking a sortable header toggles its direction, or switches to it ascending. First page again.
  const handleSort = (columnKey: string) => {
    if (columnKey === sortColumn) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
    setPage(0);
  };
  const { data: counts } = usePurchaseOrderCounts();

  // Clicking a tile filters by that status, or clears the filter when its tile is already active.
  const toggleStatus = (next: PurchaseOrderStatus) =>
    setStatus((prev) => (prev === next ? undefined : next));

  // The page stays mounted across list <-> deep-link navigation, so follow a changed :id too.
  useEffect(() => {
    if (routeOcId) setDetailOcId(routeOcId);
  }, [routeOcId]);

  const closeDetail = () => {
    setDetailOcId(null);
    // Replace (not push) so Back from the list doesn't re-open a deep-linked dialog.
    if (routeOcId) navigate("/purchase-orders", { replace: true });
  };

  const canCreate = can(ABILITIES.CREATE_PURCHASE_ORDERS);
  const canApprove = can(ABILITIES.APPROVE_PURCHASE_ORDERS);

  const resendOc = useResendOc();
  const remindApprover = useRemindApprover();

  // Resend asks for confirmation (shows the supplier); coordinate navigates to the full-page
  // coordinate flow (the single-OC form), carrying the OC id in the query.
  const [resendConfirm, setResendConfirm] =
    useState<PurchaseOrderListItem | null>(null);

  const confirmResend = () => {
    if (!resendConfirm) return;
    resendOc.mutate(resendConfirm.id, {
      onSuccess: () => {
        openNotification(t("purchaseOrders.actions.resendSuccess"));
        setResendConfirm(null);
      },
      onError: () => {
        openNotification(t("purchaseOrders.actions.resendError"));
        setResendConfirm(null);
      },
    });
  };

  const remind = (oc: PurchaseOrderListItem) =>
    remindApprover.mutate(oc.id, {
      onSuccess: () =>
        openNotification(t("purchaseOrders.actions.remindSuccess")),
      onError: () =>
        openNotification(t("purchaseOrders.actions.remindError")),
    });

  // "I'm the one who must approve this": either I'm the designated approver, or it's unassigned and
  // I hold the approve ability (any holder may approve an unassigned OC).
  const iMustApprove = (oc: PurchaseOrderListItem) =>
    oc.status === "PENDING_APPROVAL" &&
    (oc.approverUserId === user?.id ||
      (oc.approverUserId == null && canApprove));

  // The prototype's four inline row actions (Ver / Reenviar / Coordinar / Recordar). Draft "Enviar"
  // and "Anular" live in the OC detail, matching the prototype's buyer row.
  const rowActions = (oc: PurchaseOrderListItem): ReactNode => {
    const canResend = oc.status === "SENT" && canCreate;
    const canCoordinate =
      (oc.status === "SENT" || oc.status === "ACCEPTED") &&
      !oc.deliveryState &&
      canCreate;
    const canRemind =
      oc.status === "PENDING_APPROVAL" && !iMustApprove(oc);
    // Stop the click from also triggering the row's open-detail.
    return (
      <div
        className="purchase-orders__row-actions"
        onClick={(event) => event.stopPropagation()}
      >
        <IconButton
          kind="ghost"
          size="sm"
          align="left"
          label={t("purchaseOrders.actions.view")}
          onClick={() => setDetailOcId(oc.id)}
        >
          <View />
        </IconButton>
        {canResend ? (
          <IconButton
            kind="ghost"
            size="sm"
            align="left"
            label={t("purchaseOrders.actions.resend")}
            onClick={() => setResendConfirm(oc)}
          >
            <SendAlt />
          </IconButton>
        ) : null}
        {canCoordinate ? (
          <IconButton
            kind="ghost"
            size="sm"
            align="left"
            label={t("purchaseOrders.coordinate.action")}
            onClick={() =>
              navigate(`/purchase-orders/coordinate?ids=${oc.id}`)
            }
          >
            <Delivery />
          </IconButton>
        ) : null}
        {canRemind ? (
          <IconButton
            kind="ghost"
            size="sm"
            align="left"
            label={t("purchaseOrders.actions.remind")}
            onClick={() => remind(oc)}
          >
            <Notification />
          </IconButton>
        ) : null}
      </div>
    );
  };

  // The helper line under the status tag: who must approve, a missing supplier acceptance, or a
  // rejection reason — derived from the fields the list carries (Task 5).
  const statusSubline = (oc: PurchaseOrderListItem): ReactNode => {
    if (oc.status === "PENDING_APPROVAL") {
      return oc.approverName ? (
        <>
          {t("purchaseOrders.list.assignedTo")} {oc.approverName}
        </>
      ) : (
        t("purchaseOrders.list.unassigned")
      );
    }
    if (
      oc.status === "IN_DELIVERY" &&
      oc.supplierResponse !== "RESP_ACCEPTED" &&
      oc.supplierResponse !== "RESP_TACIT"
    ) {
      return t("purchaseOrders.list.supplierNotAccepted");
    }
    if (
      (oc.status === "REJECTED_SUPPLIER" ||
        oc.status === "REJECTED_APPROVER") &&
      oc.rejectionReason
    ) {
      return oc.rejectionReason;
    }
    return null;
  };

  const toRow = (oc: PurchaseOrderListItem): PurchaseOrderRow => ({
    id: oc.id,
    code: oc.code,
    purchaseRequest:
      oc.purchaseRequestCode && oc.purchaseRequestId ? (
        <Link
          to={`/purchase-requests/${oc.purchaseRequestId}`}
          onClick={(event) => event.stopPropagation()}
        >
          {oc.purchaseRequestCode}
        </Link>
      ) : (
        "-"
      ),
    supplier: oc.supplierCompanyName ?? oc.supplierName ?? "-",
    total: formatOrderTotal(oc.totalAmount, oc.currency),
    createdAt: oc.createdAt ? formatDate(oc.createdAt) : "-",
    requestedDelivery: oc.requestedDeliveryDate ? (
      <div className="purchase-orders__delivery">
        <span>{formatDate(oc.requestedDeliveryDate)}</span>
        {isOcOverdue(oc) ? (
          <span className="purchase-orders__overdue">
            <WarningAltFilled />
            {t("purchaseOrders.list.overdue")}
          </span>
        ) : null}
      </div>
    ) : (
      "-"
    ),
    status: (
      <div className="purchase-orders__status-cell">
        <OcStatusPill status={oc.status} />
        {statusSubline(oc) ? (
          <span className="purchase-orders__status-subline">
            {statusSubline(oc)}
          </span>
        ) : null}
      </div>
    ),
    actions: rowActions(oc),
  });

  const rows = (data?.content ?? []).map(toRow);

  const headers: DataTableColumn<PurchaseOrderRow>[] = [
    { key: "code", header: t("purchaseOrders.list.code"), sortable: true },
    { key: "purchaseRequest", header: t("purchaseOrders.list.purchaseRequest") },
    { key: "supplier", header: t("purchaseOrders.list.supplier"), sortable: true },
    { key: "total", header: t("purchaseOrders.list.total"), sortable: true },
    { key: "createdAt", header: t("purchaseOrders.list.createdAt"), sortable: true },
    {
      key: "requestedDelivery",
      header: t("purchaseOrders.list.requestedDelivery"),
      sortable: true,
    },
    { key: "status", header: t("purchaseOrders.list.statusColumn"), sortable: true },
    { key: "actions", header: t("purchaseOrders.list.actions") },
  ];

  const isFiltered = Boolean(status || debouncedSearchTerm);
  const clearFilters = () => {
    setStatus(undefined);
    setSearchTerm("");
  };

  const empty = isFiltered ? (
    <EmptyState
      title={t("purchaseOrders.list.noResults")}
      description={t("purchaseOrders.list.noResultsDescription")}
      action={
        <Button kind="ghost" onClick={clearFilters}>
          {t("purchaseOrders.list.clearFilters")}
        </Button>
      }
    />
  ) : (
    <EmptyState
      title={t("purchaseOrders.list.empty")}
      description={t("purchaseOrders.list.emptyDescription")}
    />
  );

  const statusItems = [
    { id: ALL_STATUSES, label: t("purchaseOrders.list.allStatuses") },
    ...PO_FILTER_STATUSES.map((value) => ({
      id: value,
      label: t(`purchaseOrders.status.${value}`),
    })),
  ];
  const selectedStatusItem =
    statusItems.find((item) => item.id === (status ?? ALL_STATUSES)) ??
    statusItems[0];

  return (
    <div>
      <PageHeader
        title={t("purchaseOrders.title")}
        action={
          canCreate ? (
            <Button onClick={() => setCreateOpen(true)}>
              {t("purchaseOrders.create.button")}
            </Button>
          ) : undefined
        }
      />
      <CreateOcModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <OcDetailModal ocId={detailOcId} onClose={closeDetail} />

      {counts ? (
        <OcStatTiles counts={counts} onSelect={toggleStatus} selected={status} />
      ) : null}

      <div className="purchase-orders__filters">
        <Dropdown
          id="purchase-orders-status"
          className="purchase-orders__status-select"
          titleText={t("purchaseOrders.list.statusFilter")}
          label={t("purchaseOrders.list.allStatuses")}
          items={statusItems}
          selectedItem={selectedStatusItem}
          itemToString={(item) => item?.label ?? ""}
          onChange={({ selectedItem }) =>
            setStatus(
              !selectedItem || selectedItem.id === ALL_STATUSES
                ? undefined
                : (selectedItem.id as PurchaseOrderStatus)
            )
          }
        />
      </div>

      <DataTable
        headers={headers}
        rows={rows}
        isLoading={isLoading}
        onRowClick={(row) => setDetailOcId(String(row.id))}
        sort={{
          columnKey: sortColumn,
          direction: sortDirection,
          onSort: handleSort,
        }}
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          label: t("purchaseOrders.list.search"),
        }}
        pagination={{
          // Carbon's Pagination is 1-indexed; the API (and this page's state) is 0-indexed.
          page: page + 1,
          pageSize,
          pageSizes: PAGE_SIZES,
          totalItems: data?.totalElements ?? 0,
          onPageChange: (newPage, newPageSize) => {
            setPage(newPage - 1);
            setPageSize(newPageSize);
          },
        }}
        empty={empty}
      />

      {resendConfirm ? (
        <ConfirmDialog
          open
          title={t("purchaseOrders.resend.confirmTitle")}
          body={t("purchaseOrders.resend.confirmBody", {
            code: resendConfirm.code,
            email:
              resendConfirm.supplierCompanyName ??
              resendConfirm.supplierName ??
              t("purchaseOrders.resend.unknownRecipient"),
          })}
          confirmLabel={t("purchaseOrders.resend.confirm")}
          isPending={resendOc.isPending}
          onCancel={() => setResendConfirm(null)}
          onConfirm={confirmResend}
        />
      ) : null}
    </div>
  );
};

export default PurchaseOrdersPage;
